import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type Suggestion } from '@/components/studio/StudioExpertReviewItem';

export type ChatMessageStatus =
  | 'queued'
  | 'processing'
  | 'review'
  | 'applied'
  | 'dismissed'
  | 'failed';

export interface ChatMessage {
  id: string;
  projectId: string | null;
  type: 'user' | 'ai';
  text: string;
  bodyText?: string;
  timestamp: number;
  status?: ChatMessageStatus;
  statusText?: string;
  statusDetail?: string;
}

export type ExpertReviewSessionStatus = 'scanning' | 'open' | 'accomplished' | 'expired' | 'failed';

export interface ExpertReviewSession {
  id: string;
  projectId: string | null;
  createdAt: number;
  focusPrompt?: string;
  summary: string;
  status: ExpertReviewSessionStatus;
  suggestionCount: number;
}

const REVIEW_SCANNING_SUMMARY =
  'Scanning overall structure, conversion flow, and missing inputs...';

const findLatestReviewSessionIndex = (
  sessions: ExpertReviewSession[],
  projectId: string | null,
  predicate: (session: ExpertReviewSession) => boolean,
) => {
  for (let index = sessions.length - 1; index >= 0; index -= 1) {
    const session = sessions[index];
    if (session.projectId === projectId && predicate(session)) {
      return index;
    }
  }
  return -1;
};

export const getExpertReviewSummary = (
  overallText: string | null | undefined,
  items: Suggestion[],
) => {
  const trimmedOverallText = overallText?.trim();
  if (trimmedOverallText) {
    return trimmedOverallText;
  }

  if (items.length === 0) {
    return 'Review notes are ready. Open the message to confirm what should happen next.';
  }

  const done = items.filter(
    (item) => item.status === 'done' || item.status === 'will_apply',
  ).length;
  const skipped = items.filter((item) => item.status === 'skipped').length;
  const openCount = items.filter(
    (item) => item.status !== 'done' && item.status !== 'will_apply' && item.status !== 'skipped',
  ).length;
  const allDone = items.length > 0 && done + skipped === items.length;

  if (allDone) {
    const doneText = done + (done !== 1 ? ' notes' : ' note') + ' confirmed';
    const skippedText = skipped ? ', ' + skipped + ' skipped' : '';
    return `AI review is complete. ${doneText}${skippedText}.`;
  }

  if (openCount > 0) {
    const suggestionText = openCount + (openCount !== 1 ? ' suggestions are' : ' suggestion is');
    return `AI notes stay optional. ${suggestionText} worth reviewing while you continue editing directly.`;
  }

  return 'Review notes are ready. Open the message to confirm what should happen next.';
};

interface ExpertState {
  projectId: string | null;
  isReviewing: boolean;
  items: Suggestion[];
  activeIndex: number;
  overallText: string;
  chatMessages: ChatMessage[];
  reviewSessions: ExpertReviewSession[];

  setProjectId: (id: string | null) => void;
  setReviewing: (val: boolean) => void;
  setItems: (items: Suggestion[]) => void;
  setActiveIndex: (index: number) => void;
  setOverallText: (text: string) => void;
  addChatMessage: (message: ChatMessage) => void;
  updateChatMessage: (id: string, updates: Partial<ChatMessage>, projectId?: string | null) => void;
  clearChatMessages: (projectId?: string | null) => void;
  startReviewSession: (projectId: string | null, focusPrompt?: string) => string | null;
  completeLatestReviewSession: (params: {
    projectId: string | null;
    summary: string;
    suggestionCount: number;
  }) => void;
  failLatestReviewSession: (projectId: string | null, summary?: string) => void;
  markLatestOpenReviewSessionAccomplished: (projectId: string | null) => void;
  clearReviewSessions: (projectId?: string | null) => void;
  resetExpertState: () => void;
}

export const useExpertStore = create<ExpertState>()(
  persist(
    (set) => ({
      projectId: null,
      isReviewing: false,
      items: [],
      activeIndex: -1,
      overallText: '',
      chatMessages: [],
      reviewSessions: [],

      setProjectId: (projectId) => set({ projectId }),
      setReviewing: (isReviewing) => set({ isReviewing }),
      setItems: (items) => set({ items }),
      setActiveIndex: (activeIndex) => set({ activeIndex }),
      setOverallText: (overallText) => set({ overallText }),

      addChatMessage: (message) =>
        set((state) => ({
          chatMessages: [...state.chatMessages, message],
        })),

      updateChatMessage: (id, updates, projectId) =>
        set((state) => ({
          chatMessages: state.chatMessages.map((msg) =>
            msg.id === id && (projectId === undefined || msg.projectId === projectId)
              ? { ...msg, ...updates }
              : msg,
          ),
        })),

      clearChatMessages: (projectId) =>
        set((state) => ({
          chatMessages:
            projectId === undefined
              ? []
              : state.chatMessages.filter((msg) => msg.projectId !== projectId),
        })),

      startReviewSession: (projectId, focusPrompt) => {
        if (!projectId) return null;

        const nextSession: ExpertReviewSession = {
          id: crypto.randomUUID(),
          projectId,
          createdAt: Date.now(),
          focusPrompt: focusPrompt?.trim() || undefined,
          summary: REVIEW_SCANNING_SUMMARY,
          status: 'scanning',
          suggestionCount: 0,
        };

        set((state) => ({
          reviewSessions: [
            ...state.reviewSessions.map((session) =>
              session.projectId === projectId &&
              (session.status === 'open' || session.status === 'scanning')
                ? { ...session, status: 'expired' as const }
                : session,
            ),
            nextSession,
          ],
        }));

        return nextSession.id;
      },

      completeLatestReviewSession: ({ projectId, summary, suggestionCount }) =>
        set((state) => {
          if (!projectId) return state;

          const nextSummary = summary.trim() || getExpertReviewSummary('', []);
          const sessionIndex = findLatestReviewSessionIndex(
            state.reviewSessions,
            projectId,
            (session) => session.status === 'scanning',
          );

          if (sessionIndex === -1) {
            return {
              reviewSessions: [
                ...state.reviewSessions,
                {
                  id: crypto.randomUUID(),
                  projectId,
                  createdAt: Date.now(),
                  summary: nextSummary,
                  status: 'open',
                  suggestionCount,
                },
              ],
            };
          }

          const reviewSessions = [...state.reviewSessions];
          reviewSessions[sessionIndex] = {
            ...reviewSessions[sessionIndex],
            summary: nextSummary,
            status: 'open',
            suggestionCount,
          };
          return { reviewSessions };
        }),

      failLatestReviewSession: (projectId, summary) =>
        set((state) => {
          if (!projectId) return state;

          const sessionIndex = findLatestReviewSessionIndex(
            state.reviewSessions,
            projectId,
            (session) => session.status === 'scanning',
          );

          if (sessionIndex === -1) {
            return state;
          }

          const reviewSessions = [...state.reviewSessions];
          reviewSessions[sessionIndex] = {
            ...reviewSessions[sessionIndex],
            summary: summary?.trim() || 'Expert review failed. Try again.',
            status: 'failed',
          };
          return { reviewSessions };
        }),

      markLatestOpenReviewSessionAccomplished: (projectId) =>
        set((state) => {
          if (!projectId) return state;

          const sessionIndex = findLatestReviewSessionIndex(
            state.reviewSessions,
            projectId,
            (session) => session.status === 'open',
          );

          if (sessionIndex === -1) {
            return state;
          }

          const reviewSessions = [...state.reviewSessions];
          reviewSessions[sessionIndex] = {
            ...reviewSessions[sessionIndex],
            status: 'accomplished',
          };
          return { reviewSessions };
        }),

      clearReviewSessions: (projectId) =>
        set((state) => ({
          reviewSessions:
            projectId === undefined
              ? []
              : state.reviewSessions.filter((session) => session.projectId !== projectId),
        })),

      resetExpertState: () =>
        set({
          projectId: null,
          isReviewing: false,
          items: [],
          activeIndex: -1,
          overallText: '',
          chatMessages: [],
          reviewSessions: [],
        }),
    }),
    {
      name: 'expert-review-storage',
      partialize: (state) => ({
        projectId: state.projectId,
        items: state.items,
        overallText: state.overallText,
        activeIndex: state.activeIndex,
        chatMessages: state.chatMessages,
        reviewSessions: state.reviewSessions.filter((session) => session.status !== 'scanning'),
      }),
    },
  ),
);
